import { Component, type ErrorInfo, type ReactNode } from "react";

/* A render error anywhere used to unmount the whole tree and leave a black
   page -- no message, no navigation, nothing to click. Selecting Apex Legends
   on /features did exactly that, because those games ship with an empty
   featureTabs array and the page indexed [0] of it.

   That specific bug is fixed, but the shape of it will happen again: this is a
   large app reading data that changes independently of the code. A boundary
   turns "the site is broken" into "this page is broken", which is the
   difference between a visitor leaving and a visitor clicking Home.

   Class component because React has no hook equivalent -- componentDidCatch
   has no functional counterpart. */

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

export default class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    /* Logged rather than swallowed: a boundary that hides the failure means
       nobody finds out the page has been dead for a week. */
    console.error("Unhandled render error:", error, info.componentStack);
  }

  render() {
    if (!this.state.error) return this.props.children;

    return (
      <div className="min-h-screen flex items-center justify-center bg-ground px-6">
        <div className="max-w-md text-center">
          <h1 className="text-2xl font-bold text-white mb-3">
            This page hit a problem
          </h1>
          <p className="text-white/50 mb-7">
            The rest of the site is fine — it is just this page. Reloading
            usually clears it.
          </p>
          <div className="flex items-center justify-center gap-3">
            <button
              onClick={() => window.location.reload()}
              className="px-6 py-3 rounded-full bg-primary hover:bg-primary-dark text-[#06211f] font-semibold transition-colors"
            >
              Reload
            </button>
            <a
              href="/"
              className="px-6 py-3 rounded-full border border-white/15 text-white/70 hover:text-white hover:border-white/30 font-semibold transition-colors"
            >
              Go home
            </a>
          </div>

          {import.meta.env.DEV && (
            <pre className="mt-8 max-h-64 overflow-auto rounded-xl border border-white/10 bg-black/40 p-4 text-left text-xs text-red-300">
              {this.state.error.message}
              {"\n\n"}
              {this.state.error.stack}
            </pre>
          )}
        </div>
      </div>
    );
  }
}
